/**
 * Aapka Essentials: animated search placeholder.
 * Only changes the placeholder. Search submission and Shopify events are untouched.
 * Each input owns one timer; sections clean up their listeners when unloaded.
 */
(() => {
  'use strict';

  const namespace = 'AapkaSearchPlaceholder';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const selector = '.header__search-inline-form .search__input';
  const controllers = new Map();
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const defaultPhrases = [
    'Search kitchen essentials…',
    'Search home décor…',
    'Search beauty products…',
    'Search gadgets…',
    'Search and more…',
  ];

  let pageSuspended = false;

  function makeController(input) {
    const listeners = new AbortController();
    const originalPlaceholder = input.getAttribute('placeholder') || 'Search products';
    let phrases = defaultPhrases;

    // Optional JSON array on the input supports translated or custom phrases.
    if (input.dataset.aapkaSearchPhrases) {
      try {
        const candidate = JSON.parse(input.dataset.aapkaSearchPhrases);
        if (Array.isArray(candidate)) {
          const strings = candidate.filter((item) => typeof item === 'string' && item.trim());
          if (strings.length > 0) {
            phrases = strings;
          }
        }
      } catch {
        // Keep the default phrases if the optional attribute is malformed.
      }
    }

    let timer = null;
    let phraseIndex = 0;
    let characterIndex = 0;
    let deleting = false;

    function stop() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function canAnimate() {
      return (
        input.isConnected &&
        !pageSuspended &&
        !document.hidden &&
        !motionPreference.matches &&
        document.activeElement !== input &&
        input.value === ''
      );
    }

    function schedule(delay) {
      stop();
      if (canAnimate()) {
        timer = window.setTimeout(tick, delay);
      }
    }

    function tick() {
      timer = null;
      if (!canAnimate()) {
        return;
      }

      const phrase = phrases[phraseIndex];
      characterIndex += deleting ? -1 : 1;
      characterIndex = Math.max(0, Math.min(characterIndex, phrase.length));
      input.placeholder = phrase.slice(0, characterIndex);

      let delay = deleting ? 35 : 75;
      if (!deleting && characterIndex === phrase.length) {
        deleting = true;
        delay = 2200;
      } else if (deleting && characterIndex === 0) {
        deleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        delay = 450;
      }

      schedule(delay);
    }

    function refresh() {
      stop();
      if (motionPreference.matches || document.activeElement === input) {
        input.placeholder = originalPlaceholder;
      }
      if (canAnimate()) {
        schedule(450);
      }
    }

    input.addEventListener('focus', refresh, { signal: listeners.signal });
    input.addEventListener('blur', refresh, { signal: listeners.signal });
    input.addEventListener('input', refresh, { signal: listeners.signal });
    input.addEventListener('search', refresh, { signal: listeners.signal });
    input.addEventListener('change', refresh, { signal: listeners.signal });

    refresh();

    return {
      refresh,
      stop,
      destroy() {
        stop();
        listeners.abort();
        input.placeholder = originalPlaceholder;
      },
    };
  }

  function removeDisconnected() {
    for (const [input, controller] of controllers) {
      if (!input.isConnected) {
        controller.destroy();
        controllers.delete(input);
      }
    }
  }

  function init(scope) {
    removeDisconnected();
    const inputs = [];
    if (scope.matches && scope.matches(selector)) {
      inputs.push(scope);
    }
    if (scope.querySelectorAll) {
      inputs.push(...scope.querySelectorAll(selector));
    }
    for (const input of inputs) {
      if (!controllers.has(input)) {
        controllers.set(input, makeController(input));
      }
    }
  }

  function refreshAll() {
    removeDisconnected();
    for (const controller of controllers.values()) {
      controller.refresh();
    }
  }

  window[namespace] = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [input, controller] of controllers) {
      if (event.target === input || event.target.contains(input)) {
        controller.destroy();
        controllers.delete(input);
      }
    }
  });
  document.addEventListener('visibilitychange', refreshAll);
  motionPreference.addEventListener('change', refreshAll);

  window.addEventListener('pagehide', () => {
    pageSuspended = true;
    for (const controller of controllers.values()) {
      controller.stop();
    }
  });
  window.addEventListener('pageshow', () => {
    pageSuspended = false;
    refreshAll();
  });
})();
